import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import type { Node } from 'reactflow';
import type { TriggerNodeData } from './TriggerNode/TriggerNode';
import type { ManualVariable, TriggerSettings } from './triggerWorkflowTypes';
import { buildTriggerPreview, webhookUrlPreview } from './triggerWorkflowTypes';
import { WorkflowConfigPanelMaximizeButton } from './WorkflowConfigPanelFrame';

type TriggerConfigPanelProps = {
  node: Node<TriggerNodeData & { settings: TriggerSettings }>;
  panelMaximized?: boolean;
  onTogglePanelMaximize?: () => void;
  onUpdate: (nodeId: string, data: Partial<TriggerNodeData & { settings: TriggerSettings }>) => void;
  onDelete: (nodeId: string) => void;
  onClose: () => void;
};

const VAR_TYPES = ['string', 'number', 'boolean', 'object', 'array'] as const;

export default function TriggerConfigPanel({
  node,
  panelMaximized = false,
  onTogglePanelMaximize,
  onUpdate,
  onDelete,
  onClose,
}: TriggerConfigPanelProps) {
  const [local, setLocal] = useState(node.data.settings);

  useEffect(() => {
    setLocal(node.data.settings);
  }, [node.id, node.data.settings]);

  const pushUpdate = (next: TriggerSettings) => {
    setLocal(next);
    const preview = buildTriggerPreview(next.kind, next);
    onUpdate(node.id, { settings: next, preview });
  };

  const outputsBlock = useMemo(() => {
    if (local.kind === 'manual') {
      const lines: string[] = [];
      const seen = new Set<string>();
      for (const v of local.variables) {
        const n = v.name?.trim();
        if (!n || seen.has(n)) continue;
        seen.add(n);
        lines.push(`{{ trigger.variables.${n} }}`);
      }
      return lines.join('\n');
    }
    const lines = ['{{ trigger.timestamp }}'];
    if (local.kind === 'webhook' || local.kind === 'event') {
      lines.push('{{ trigger.payload }}');
    }
    return lines.join('\n');
  }, [local]);

  return (
    <aside className="workflow-trigger-panel" aria-label="Trigger configuration">
      <div className="workflow-trigger-panel__head">
        <h2 className="workflow-trigger-panel__title">Trigger</h2>
        <div className="workflow-trigger-panel__head-actions">
          {onTogglePanelMaximize ? (
            <WorkflowConfigPanelMaximizeButton expanded={panelMaximized} onToggle={onTogglePanelMaximize} />
          ) : null}
          <button type="button" className="workflow-trigger-panel__close" onClick={onClose} aria-label="Close panel">
            <X size={18} strokeWidth={2} />
          </button>
        </div>
      </div>
      <p className="workflow-trigger-panel__subtitle">{node.data.label}</p>

      <div className="workflow-trigger-panel__section">
        <label className="workflow-trigger-panel__label" htmlFor={`trigger-label-${node.id}`}>
          Label
        </label>
        <input
          id={`trigger-label-${node.id}`}
          className="workflow-trigger-panel__input"
          value={node.data.label}
          onChange={(e) => onUpdate(node.id, { label: e.target.value })}
        />
      </div>

      {local.kind === 'manual' && (
        <ManualFields
          variables={local.variables}
          onChange={(variables) => pushUpdate({ kind: 'manual', variables })}
        />
      )}

      {local.kind === 'webhook' && (
        <WebhookFields
          path={local.path}
          auth={local.auth}
          onChange={(path, auth) => pushUpdate({ kind: 'webhook', path, auth })}
        />
      )}

      {local.kind === 'scheduled' && (
        <ScheduledFields nodeId={node.id} settings={local} onChange={pushUpdate} />
      )}

      {local.kind === 'event' && (
        <div className="workflow-trigger-panel__section">
          <label className="workflow-trigger-panel__label" htmlFor={`event-pattern-${node.id}`}>
            Event name pattern
          </label>
          <input
            id={`event-pattern-${node.id}`}
            className="workflow-trigger-panel__input"
            value={local.pattern}
            onChange={(e) => pushUpdate({ kind: 'event', pattern: e.target.value })}
            placeholder="lead.*"
          />
        </div>
      )}

      <div className="workflow-trigger-panel__section">
        <span className="workflow-trigger-panel__label">
          {local.kind === 'manual' ? 'Output variables' : 'Outputs'}
        </span>
        {outputsBlock ? (
          <pre className="workflow-trigger-panel__outputs">{outputsBlock}</pre>
        ) : local.kind === 'manual' ? (
          <p className="workflow-trigger-panel__muted">Add named input variables above to list output templates.</p>
        ) : null}
      </div>

      <div className="workflow-trigger-panel__actions">
        <button type="button" className="workflow-trigger-panel__btn workflow-trigger-panel__btn--danger" onClick={() => onDelete(node.id)}>
          Delete trigger
        </button>
      </div>
    </aside>
  );
}

function ManualFields({
  variables,
  onChange,
}: {
  variables: ManualVariable[];
  onChange: (v: ManualVariable[]) => void;
}) {
  const updateRow = (i: number, patch: Partial<ManualVariable>) => {
    const next = variables.map((row, j) => (j === i ? { ...row, ...patch } : row));
    onChange(next);
  };
  const add = () => onChange([...variables, { name: '', type: 'string' }]);
  const remove = (i: number) => onChange(variables.filter((_, j) => j !== i));

  return (
    <div className="workflow-trigger-panel__section">
      <div className="workflow-trigger-panel__row-between">
        <span className="workflow-trigger-panel__label">Input variables</span>
        <button type="button" className="workflow-trigger-panel__btn-link" onClick={add}>
          + Add
        </button>
      </div>
      {variables.length === 0 && <p className="workflow-trigger-panel__muted">No variables yet.</p>}
      <ul className="workflow-trigger-panel__var-list">
        {variables.map((row, i) => (
          <li key={i} className="workflow-trigger-panel__var-row">
            <input
              className="workflow-trigger-panel__input workflow-trigger-panel__input--grow"
              placeholder="name"
              value={row.name}
              onChange={(e) => updateRow(i, { name: e.target.value })}
            />
            <select
              className="workflow-trigger-panel__select"
              value={row.type}
              onChange={(e) => updateRow(i, { type: e.target.value })}
            >
              {VAR_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <button type="button" className="workflow-trigger-panel__btn-icon" onClick={() => remove(i)} aria-label="Remove variable">
              ×
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function WebhookFields({
  path,
  auth,
  onChange,
}: {
  path: string;
  auth: 'none' | 'apiKey' | 'hmac';
  onChange: (path: string, auth: 'none' | 'apiKey' | 'hmac') => void;
}) {
  return (
    <>
      <div className="workflow-trigger-panel__section">
        <label className="workflow-trigger-panel__label">Path</label>
        <input
          className="workflow-trigger-panel__input"
          value={path}
          onChange={(e) => onChange(e.target.value, auth)}
          placeholder="/api/leads"
        />
      </div>
      <div className="workflow-trigger-panel__section">
        <label className="workflow-trigger-panel__label">Auth</label>
        <select className="workflow-trigger-panel__select" value={auth} onChange={(e) => onChange(path, e.target.value as typeof auth)}>
          <option value="none">none</option>
          <option value="apiKey">apiKey</option>
          <option value="hmac">hmac</option>
        </select>
      </div>
      <div className="workflow-trigger-panel__section">
        <span className="workflow-trigger-panel__label">URL preview</span>
        <pre className="workflow-trigger-panel__preview-url">{webhookUrlPreview(path)}</pre>
      </div>
    </>
  );
}

function ScheduledFields({
  nodeId,
  settings,
  onChange,
}: {
  nodeId: string;
  settings: Extract<TriggerSettings, { kind: 'scheduled' }>;
  onChange: (s: Extract<TriggerSettings, { kind: 'scheduled' }>) => void;
}) {
  const radioName = `sched-mode-${nodeId}`;
  return (
    <>
      <div className="workflow-trigger-panel__section">
        <span className="workflow-trigger-panel__label">Schedule</span>
        <div className="workflow-trigger-panel__radio-row">
          <label className="workflow-trigger-panel__radio">
            <input
              type="radio"
              name={radioName}
              checked={settings.scheduleMode === 'simple'}
              onChange={() =>
                onChange({
                  ...settings,
                  scheduleMode: 'simple',
                })
              }
            />
            Simple
          </label>
          <label className="workflow-trigger-panel__radio">
            <input
              type="radio"
              name={radioName}
              checked={settings.scheduleMode === 'cron'}
              onChange={() =>
                onChange({
                  ...settings,
                  scheduleMode: 'cron',
                })
              }
            />
            Cron
          </label>
        </div>
      </div>

      {settings.scheduleMode === 'cron' ? (
        <div className="workflow-trigger-panel__section">
          <label className="workflow-trigger-panel__label">Cron expression</label>
          <input
            className="workflow-trigger-panel__input"
            value={settings.cronExpression}
            onChange={(e) => onChange({ ...settings, cronExpression: e.target.value })}
            placeholder="0 9 * * *"
          />
        </div>
      ) : (
        <>
          <div className="workflow-trigger-panel__section">
            <label className="workflow-trigger-panel__label">Every</label>
            <div className="workflow-trigger-panel__inline">
              <input
                type="number"
                min={1}
                className="workflow-trigger-panel__input workflow-trigger-panel__input--narrow"
                value={settings.simpleInterval}
                onChange={(e) =>
                  onChange({
                    ...settings,
                    simpleInterval: Math.max(1, parseInt(e.target.value, 10) || 1),
                  })
                }
              />
              <select
                className="workflow-trigger-panel__select"
                value={settings.simpleUnit}
                onChange={(e) =>
                  onChange({
                    ...settings,
                    simpleUnit: e.target.value as 'hours' | 'days',
                  })
                }
              >
                <option value="hours">hours</option>
                <option value="days">days</option>
              </select>
            </div>
          </div>
          {settings.simpleUnit === 'days' && (
            <div className="workflow-trigger-panel__section">
              <label className="workflow-trigger-panel__label">Time (local)</label>
              <input
                type="time"
                className="workflow-trigger-panel__input"
                value={settings.dailyTime}
                onChange={(e) => onChange({ ...settings, dailyTime: e.target.value })}
              />
            </div>
          )}
        </>
      )}
    </>
  );
}
